import { useEffect } from 'react';
import clsx from 'clsx';
import { useAppointmentSchedulerStore } from '../../stores/appointment-scheduler-store';
import { formatDate } from '../../lib/format';
import { CustomerContextHeader } from './CustomerContextHeader';
import { RouteOptionList } from './RouteOptionList';
import { CapacityBumpCard } from './CapacityBumpCard';
import { BypassPostcodegebiedCard } from './BypassPostcodegebiedCard';
import { AlsoDeliverToggle } from './AlsoDeliverToggle';
import { OpslagkostenWaiver } from './OpslagkostenWaiver';

export function AppointmentScheduler() {
  const isOpen = useAppointmentSchedulerStore((s) => s.isOpen);
  const order = useAppointmentSchedulerStore((s) => s.order);
  const primaryType = useAppointmentSchedulerStore((s) => s.primaryType);
  const routes = useAppointmentSchedulerStore((s) => s.routes);
  const selectedRouteId = useAppointmentSchedulerStore((s) => s.selectedRouteId);
  const selectRoute = useAppointmentSchedulerStore((s) => s.selectRoute);
  const notes = useAppointmentSchedulerStore((s) => s.notes);
  const setNotes = useAppointmentSchedulerStore((s) => s.setNotes);
  const informerenKlant = useAppointmentSchedulerStore((s) => s.informerenKlant);
  const setInformerenKlant = useAppointmentSchedulerStore((s) => s.setInformerenKlant);
  const isLoadingRoutes = useAppointmentSchedulerStore((s) => s.isLoadingRoutes);
  const routesError = useAppointmentSchedulerStore((s) => s.routesError);
  const showExtraRoutes = useAppointmentSchedulerStore((s) => s.showExtraRoutes);
  const toggleShowExtra = useAppointmentSchedulerStore((s) => s.toggleShowExtra);
  const alsoDeliver = useAppointmentSchedulerStore((s) => s.alsoDeliver);
  const deliverySelectedRouteId = useAppointmentSchedulerStore((s) => s.deliverySelectedRouteId);
  const isSubmitting = useAppointmentSchedulerStore((s) => s.isSubmitting);
  const submitResult = useAppointmentSchedulerStore((s) => s.submitResult);
  const submit = useAppointmentSchedulerStore((s) => s.submit);
  const close = useAppointmentSchedulerStore((s) => s.close);
  const openCapacityBump = useAppointmentSchedulerStore((s) => s.openCapacityBump);
  const capacityBumpOpen = useAppointmentSchedulerStore((s) => s.capacityBumpOpen);
  const bypassActive = useAppointmentSchedulerStore((s) => s.bypassActive);
  const bypassConfirmOpen = useAppointmentSchedulerStore((s) => s.bypassConfirmOpen);
  const openBypassConfirm = useAppointmentSchedulerStore((s) => s.openBypassConfirm);
  const bypassRouteNaamFilter = useAppointmentSchedulerStore((s) => s.bypassRouteNaamFilter);
  const setBypassRouteNaamFilter = useAppointmentSchedulerStore((s) => s.setBypassRouteNaamFilter);
  const waiverInPaneel = useAppointmentSchedulerStore((s) => s.geenOpslagkosten);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !capacityBumpOpen && !bypassConfirmOpen) close();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, close, capacityBumpOpen, bypassConfirmOpen]);

  useEffect(() => {
    if (submitResult?.ok) {
      const timer = setTimeout(() => close(), 2500);
      return () => clearTimeout(timer);
    }
  }, [submitResult?.ok, close]);

  if (!isOpen || !order) return null;

  const title = primaryType === 'pickup' ? 'Ophalen inplannen' : 'Afleveren inplannen';
  // Opslag telt niet als de WO de vlag al draagt (via Opties op de order) óf als
  // het kantoor hem in dit paneel aanzet. Anders toont de lijst "+€10 opslag"
  // op een order die het nooit gaat betalen.
  const geenOpslagkosten = order.geenOpslagkosten === true || waiverInPaneel;
  const canSubmit = !isSubmitting
    && !submitResult?.ok
    && !!selectedRouteId
    && (!alsoDeliver || !!deliverySelectedRouteId);

  // Capaciteit-bump-card verschijnt als: alle zichtbare routes hebben ≤3 capaciteit
  // (volle routes zijn nu zichtbaar via badges), er ≥1 route is, en de capaciteit-
  // bump-modal niet al open staat.
  const visibleCount = showExtraRoutes ? routes.length : Math.min(routes.length, 3);
  const allTight = visibleCount > 0
    && routes.slice(0, visibleCount).every((r) => r.capaciteit <= 3);
  const showTightHint = allTight && !capacityBumpOpen;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-[65] transition-opacity"
        onClick={() => { if (!capacityBumpOpen && !bypassConfirmOpen) close(); }}
      />

      <div
        className={clsx(
          'fixed top-0 right-0 h-full w-[480px] max-w-full bg-white shadow-2xl z-[70] flex flex-col',
          'transition-transform duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={close}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <CustomerContextHeader order={order} />

        {submitResult?.ok ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-slate-900 font-medium">
              {submitResult.message || 'Afspraak ingepland'}
            </p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {submitResult && !submitResult.ok && (
                <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {submitResult.message}
                </div>
              )}

              {(() => {
                const datumWindow = primaryType === 'pickup'
                  ? order.pickupVanafDatum
                  : order.deliveryVanafDatum;
                if (!datumWindow) return null;
                return (
                  <div className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Vroegste {primaryType === 'pickup' ? 'ophaaldatum' : 'afleverdatum'}:{' '}
                    <strong>{formatDate(datumWindow)}</strong>. Eerdere routes worden niet getoond.
                  </div>
                );
              })()}

              <RouteOptionList
                routes={routes}
                selectedId={selectedRouteId}
                onSelect={selectRoute}
                showExtra={showExtraRoutes}
                onToggleExtra={toggleShowExtra}
                isLoading={isLoadingRoutes}
                error={routesError}
                onBumpCapacity={(routeId) => openCapacityBump(routeId, primaryType)}
                bypassActive={bypassActive}
                onEnableBypass={!capacityBumpOpen && !bypassConfirmOpen ? openBypassConfirm : undefined}
                bypassRouteNaamFilter={bypassRouteNaamFilter}
                onChangeBypassRouteNaamFilter={setBypassRouteNaamFilter}
                opslagkostenStartDatum={primaryType === 'delivery' ? order.opslagkostenStartDatum : null}
                showOpslag={primaryType === 'delivery' && !geenOpslagkosten}
              />

              {primaryType === 'delivery' && order.opslagkostenStartDatum && !geenOpslagkosten && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2">
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Opslag is gratis voor de eerste 28 dagen. Daarna rekenen we €10 per begonnen week.</span>
                </div>
              )}

              {primaryType === 'delivery' && <OpslagkostenWaiver />}

              {showTightHint && routes.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Weinig plekken beschikbaar. Klik bij een route op "Capaciteit +" om er meer plekken toe te voegen.
                </p>
              )}

              <CapacityBumpCard />
              <BypassPostcodegebiedCard />

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600 block">
                  Opmerkingen voor chauffeur
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Bijv. bel 10 min van tevoren..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              {order && (
                (primaryType === 'pickup' && order.status === 'Ophalen ingepland') ||
                (primaryType === 'delivery' && order.status === 'Terugbrengen ingepland')
              ) && (
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={informerenKlant}
                    onChange={(e) => setInformerenKlant(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>
                    Klant informeren over deze wijziging
                    <span className="block text-slate-400">Stuurt bevestigingsmail met nieuw tijdstip. Uitzetten bij interne correctie.</span>
                  </span>
                </label>
              )}

              {primaryType === 'pickup' && <AlsoDeliverToggle />}
            </div>

            <div className="px-5 py-4 border-t border-slate-200 bg-white flex gap-3">
              <button
                type="button"
                onClick={close}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className={clsx(
                  'flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
                  canSubmit
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                )}
              >
                {isSubmitting
                  ? 'Bezig...'
                  : bypassActive
                    ? 'Bevestig afwijkende afspraak'
                    : 'Bevestig afspraak'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
